/**
 * Paddle Payment Integration
 * Handles Paddle checkout and subscription management
 */

declare global {
  interface Window {
    Paddle?: any;
  }
}

const PADDLE_VENDOR_ID = import.meta.env.VITE_PADDLE_VENDOR_ID;
const PADDLE_ENVIRONMENT = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';
const PADDLE_CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;

/**
 * Initialize Paddle
 */
export function initializePaddle(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if credentials are set
    if (!PADDLE_CLIENT_TOKEN) {
      console.error('Paddle client token is missing');
      reject(new Error('Paddle client token is not configured'));
      return;
    }

    console.log('Initializing Paddle with:', {
      environment: PADDLE_ENVIRONMENT,
      vendorId: PADDLE_VENDOR_ID,
      hasToken: !!PADDLE_CLIENT_TOKEN,
    });

    if (window.Paddle) {
      console.log('Paddle already initialized');
      resolve();
      return;
    }

    const script = document.createElement('script');
    const paddleScriptUrl = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.src = paddleScriptUrl;
    script.async = true;
    
    script.onload = () => {
      console.log('Paddle script loaded. Setting environment to:', PADDLE_ENVIRONMENT);
      if (window.Paddle) {
        try {
          // 1. Tell Paddle what backend servers to look for BEFORE initializing
          if (PADDLE_ENVIRONMENT === 'sandbox') {
            window.Paddle.Environment.set('sandbox'); 
          }

          // 2. Initialize using only the accepted parameter configurations
          window.Paddle.Initialize({
            token: PADDLE_CLIENT_TOKEN, 
          });
          
          console.log('Paddle initialized successfully');
          resolve();
        } catch (error) {
          console.error('Error initializing Paddle:', error);
          reject(error);
        }
      } else {
        console.error('Paddle object not found after script load');
        reject(new Error('Paddle failed to load'));
      }
    };
    
    script.onerror = (error) => {
      console.error('Failed to load Paddle script:', error);
      reject(new Error('Failed to load Paddle script'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Open Paddle checkout for subscription
 */
export async function openPaddleCheckout(
  priceId: string,
  userId: string,
  userEmail: string,
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  try {
    console.log('Opening Paddle checkout with:', {
      priceId,
      userId,
      userEmail,
    });

    await initializePaddle();

    if (!window.Paddle) {
      throw new Error('Paddle not initialized');
    }

    console.log('Opening checkout...');
    
    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: {
        email: userEmail,
      },
      customData: {
        userId,
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en',
        successUrl: `${window.location.origin}/subscription?success=true&provider=paddle`,
      },
      eventCallback: (event: any) => {
        console.log('Paddle event:', event);
        if (event.name === 'checkout.completed') {
          onSuccess?.(event.data);
        } else if (event.name === 'checkout.error') {
          onError?.(event.data);
        }
      },
    });
  } catch (error) {
    console.error('Paddle checkout error:', error);
    onError?.(error);
    throw error;
  }
}

/**
 * Get Paddle customer portal URL
 */
export async function getPaddlePortalUrl(): Promise<string> {
  try {
    // This would typically call your backend to generate a portal session
    const response = await fetch('/api/paddle-portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Failed to get portal URL');
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('Error getting Paddle portal URL:', error);
    throw error;
  }
}

/**
 * Cancel Paddle subscription
 */
export async function cancelPaddleSubscription(subscriptionId: string): Promise<void> {
  try {
    const response = await fetch('/api/paddle-cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionId }),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }
  } catch (error) {
    console.error('Error canceling Paddle subscription:', error);
    throw error;
  }
}

export const paddleConfig = {
  vendorId: PADDLE_VENDOR_ID,
  environment: PADDLE_ENVIRONMENT,
  // Define your product/price IDs here
  products: {
    pro: {
      monthly: 'pri_01ktg56vv0w0wv52f973ymtbby', // Replace with your production price ID from Paddle
      name: 'Pro Monthly',
      price: '$5',
    },
  },
};
